import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useDialogFocus } from './useDialogFocus';
import './Help.css';

export interface TourStep { target: string; title: string; body: string; route?: string }
interface Props { steps: TourStep[]; onComplete: (result: 'dismissed' | 'finished') => Promise<void>; targetTimeout?: number }

/** Guide a user across visible, route-aware targets without changing their data. */
export default function GuidedTour({ steps, onComplete, targetTimeout = 3000 }: Props) {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dialog = useRef<HTMLDivElement>(null);
  const completion = useRef(false);
  const step = steps[index];
  useDialogFocus(dialog);
  const finish = useCallback(async (result: 'dismissed' | 'finished') => {
    if (completion.current) return;
    completion.current = true; setSaving(true); setError('');
    try { await onComplete(result); if (result === 'finished') navigate(steps.some(s => s.route?.startsWith('/portal')) ? '/portal' : '/'); }
    catch { setError('Could not save your tour progress. Please try again.'); completion.current = false; setSaving(false); }
  }, [onComplete, navigate, steps]);
  useEffect(() => {
    if (completion.current) return;
    if (!step) { void finish('finished'); return; }
    setRect(null);
    if (step.route) navigate(step.route);
    const started = Date.now();
    let scrolled = false;
    const position = () => {
      const target = document.querySelector<HTMLElement>(`[data-tour="${step.target}"]`);
      if (target) {
        const bounds = target.getBoundingClientRect();
        if (bounds.width && bounds.height && getComputedStyle(target).visibility !== 'hidden') {
          if (!scrolled) { target.scrollIntoView?.({ block: 'center', behavior: 'auto' }); scrolled = true; }
          const next = target.getBoundingClientRect();
          if (next.bottom > 0 && next.top < window.innerHeight && next.right > 0 && next.left < window.innerWidth) { setRect(next); return true; }
        }
      }
      return false;
    };
    position();
    const timer = window.setInterval(() => {
      if (!position() && Date.now() - started >= targetTimeout) setIndex(value => value + 1);
    }, 100);
    const reposition = () => { position(); };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => { clearInterval(timer); window.removeEventListener('resize', reposition); window.removeEventListener('scroll', reposition, true); };
  }, [step, navigate, targetTimeout, finish]);
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (saving) return;
      if (['Escape', 'ArrowRight', 'ArrowLeft'].includes(event.key)) event.preventDefault();
      if (event.key === 'Escape') void finish('dismissed');
      if (event.key === 'ArrowRight') { if (index === steps.length - 1) void finish('finished'); else setIndex(value => value + 1); }
      if (event.key === 'ArrowLeft') setIndex(value => Math.max(0, value - 1));
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [finish, saving, index, steps.length]);
  const mobile = window.matchMedia?.('(max-width: 767px)').matches ?? window.innerWidth < 768;
  const left = rect ? Math.max(12, Math.min(rect.left, window.innerWidth - 372)) : 12;
  const top = rect ? Math.max(12, Math.min(rect.bottom + 12, window.innerHeight - 280)) : 80;
  return createPortal(<div className="tour-layer">
    <div className="tour-blocker" />
    {rect && <div className="tour-spotlight" style={{ top: Math.max(0, rect.top - 4), left: Math.max(0, rect.left - 4), width: Math.min(rect.width + 8, window.innerWidth - Math.max(0, rect.left - 4)), height: Math.min(rect.height + 8, window.innerHeight) }} />}
    <div ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="tour-title" className="help-card tour-card" style={mobile ? undefined : { left, top }}>
      <p aria-live="polite">Step {Math.min(index + 1, steps.length)} of {steps.length}</p>
      <h2 id="tour-title">{rect ? step?.title : 'Getting your tour ready'}</h2>
      <p>{rect ? step?.body : 'Looking for the next available part of the page…'}</p>
      {error && <p role="alert">{error}</p>}
      <div className="help-buttons">
        <button disabled={saving || index === 0} onClick={() => setIndex(value => value - 1)}>Back</button>
        <button disabled={saving} onClick={() => void finish('dismissed')}>Skip tour</button>
        <button disabled={saving || (!rect && !!step)} onClick={() => index >= steps.length - 1 ? void finish('finished') : setIndex(value => value + 1)}>{index >= steps.length - 1 ? 'Finish' : 'Next'}</button>
      </div>
    </div>
  </div>, document.body);
}
